import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {ITween} from "../core/Animatable";
import {BitmapText} from "../core/BitmapText";
import {BaseButton} from "../core/Button";
import {ContainerExtensions} from "../core/ContainerExtensions";
import {IPoint, IRectangle} from "../core/Geom";
import {Callback} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import {PlayMode, VoiceOverManager} from "../managers/VoiceOverManager";
import {Focusable, IFocusableRect} from "./Focusable";
import {KeyboardFocusLayer} from "./KeyboardFocusLayer";

export class StoryCaption extends BitmapText {

    private _timeoutTween?: ITween;

    public initVO(vo: string | Array<string | number>, timeout: number = 8, firstCallback?: Callback) {
        timeout = 0; // never replay VO
        this.playVO(vo, timeout, firstCallback);
    }

    public destroy(options?: PIXI.DestroyOptions | boolean): void {
        if (this._timeoutTween) {
            this._timeoutTween!.kill();
        }
        super.destroy(options);
    }

    public playVO(vo: string | Array<string | number>, timeout: number, cb?: Callback) {
        VoiceOverManager.instance.playVO(vo, {
            mode: PlayMode.New,
            skipCC: true,
            cb: timeout > 0 ? () => {
                if (this.parent) {
                    if (this._timeoutTween) {
                        this._timeoutTween.kill();
                    }
                    this._timeoutTween = this.delayedCall(timeout, () => {
                        this.playVO(vo, timeout);
                    });
                }
                if (cb) {
                    cb();
                }
            } : cb,
        });
    }
}

export class ClickableStoryCaption extends StoryCaption implements IFocusableRect {

    private _captionHitArea?: Sprite;

    public initVO(vo: string | string[], timeout: number = 8, firstCallback?: () => void) {
        timeout = 0; // never replay VO
        super.initVO(vo, timeout, firstCallback);
        if (!this._captionHitArea) {
            this._captionHitArea = this.parent.addChildAt(new Sprite(undefined, this.anchor as IPoint), this.zIndex);
            this._captionHitArea.x = this.x;
            this._captionHitArea.y = this.y;
            this._captionHitArea.width = this.width;
            this._captionHitArea.height = this.height;
        }
        this._captionHitArea.buttonMode = true;
        this._captionHitArea.interactive = true;
        this._captionHitArea.off("pointertap");
        this._captionHitArea.on("pointertap", () => {
            this.playVO(vo, timeout);
        });
    }

    public onFocusBegin(this: HLF.Input.IFocusable & BaseButton): void {
        KeyboardFocusLayer.instance.currentFocusable = this;
    }

    public onFocusEnd(this: BaseButton): void {
        KeyboardFocusLayer.instance.currentFocusable = undefined;
    }

    public onFocusActivated(): void {
        if (this._captionHitArea) {
            this._captionHitArea.emit("pointertap");
        }
    }

    public getFocusPosition(): PIXI.Point {
        return Focusable.getFocusPosition.call(this._captionHitArea || this as PIXI.Container);
    }

    public getFocusSize(): PIXI.Point {
        return Focusable.getFocusSize.call(this._captionHitArea || this as PIXI.Container);
    }

    public getFocusRect(): IRectangle {
        return ContainerExtensions.getGlobalBounds.call(this._captionHitArea || this as PIXI.Container);
    }

    public isFocusable(): boolean {
        return !!this._captionHitArea;
    }
}
